/**
 * 大六壬推演引擎（时间起课）
 *
 * 入口：daliurenDivination(date, opts?) → DaLiuRenResult
 * 纯函数、确定性：同一时刻（精确到秒）+ 同一可选参数必得同一盘面，双端 + Worker 共用。
 *
 * 排盘顺序：
 *   1. 四柱（年 / 月 / 日 / 时）
 *      - 年柱：立春换年（getYearGanIndexExact）
 *      - 月柱：节气换月（getMonthGanIndexExact）
 *      - 日柱：23:00 换日（getDayGanIndexExact）
 *      - 时柱：五鼠遁，时干 = (日干序 × 2 + 时支序) % 10
 *   2. 月将：中气换将（jieQi.currentYueJiang）
 *   3. 天地盘：月将加占时（时支宫），顺布十二支
 *   4. 四课：日干寄宫 + 日支，逐一取上神
 *   5. 三传：九宗门（sanChuan.computeSanChuan）
 *   6. 空亡：日柱旬空
 *   7. 十二天将：贵人诀 + 昼夜 + 顺逆布将
 *   8. 六亲 / 遁干 / 长生 / 神煞 / 课体
 *   9. 应期（通行简法）；提供出生年份时另推本命 / 行年，并纳入断课
 */
import { Lunar } from 'lunar-javascript';
import { computeBenMing } from './benming';
import { ZHI, changSheng, dunGan, ganIndex, ganOf, isYangGan, jiGong, xunKong, xunName, zhiIndex, zhiOf, } from './constants';
import { interpret } from './interpret';
import { currentYueJiang } from './jieQi';
import { buildKeTi } from './keti';
import { liuQinOfZhi } from './liuqin';
import { computeSanChuan } from './sanChuan';
import { buildShenSha } from './shensha';
import { buildTianJiang } from './tianJiang';
import { computeYingQi } from './yingqi';
const KE_NAMES = ['干阳课', '干阴课', '支阳课', '支阴课'];
const CHUAN_NAMES = ['初传', '中传', '末传'];
/** 构造一柱干支 */
function gz(ganIdx, zhiIdx) {
    const gan = ganOf(ganIdx);
    const zhi = zhiOf(zhiIdx);
    return { gan, zhi, ganIndex: ganIdx, zhiIndex: zhiIdx, text: `${gan}${zhi}` };
}
/**
 * 大六壬起课（时间起课）。
 * @param date 占卜时刻（默认当前时间）；同一时刻 + 同一 opts 必得同一盘面
 * @param opts 可选参数（本命出生年份 / 性别）；出生年份非法时忽略本命行年，不影响排盘
 */
export function daliurenDivination(date = new Date(), opts = {}) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        throw new TypeError('大六壬起课需要合法的 Date 时刻');
    }
    const lunar = Lunar.fromDate(date);
    // ── 1. 四柱 ──
    const year = gz(lunar.getYearGanIndexExact(), lunar.getYearZhiIndexExact());
    const month = gz(lunar.getMonthGanIndexExact(), lunar.getMonthZhiIndexExact());
    const day = gz(lunar.getDayGanIndexExact(), lunar.getDayZhiIndexExact());
    const zhanShiIndex = lunar.getTimeZhiIndex();
    const hour = gz((day.ganIndex * 2 + zhanShiIndex) % 10, zhanShiIndex);
    const siZhu = { year, month, day, hour };
    const siZhuText = `${year.text}年 ${month.text}月 ${day.text}日 ${hour.text}时`;
    // ── 2. 月将（中气换将） ──
    const { yueJiang, qi, qiTime } = currentYueJiang(date);
    const yueJiangIndex = zhiIndex(yueJiang.zhi);
    // ── 3. 天地盘：月将加于占时之上，顺布 ──
    const tianPanIdx = [];
    for (let p = 0; p < 12; p += 1) {
        tianPanIdx.push((((yueJiangIndex + p - zhanShiIndex) % 12) + 12) % 12);
    }
    const tianPan = tianPanIdx.map((i) => zhiOf(i));
    // ── 4. 四课 ──
    const ganGong = jiGong(day.ganIndex);
    const keInputs = [
        { shangIdx: tianPanIdx[ganGong], xiaIdx: day.ganIndex, xiaType: 'gan' },
        { shangIdx: 0, xiaIdx: 0, xiaType: 'zhi' },
        { shangIdx: tianPanIdx[day.zhiIndex], xiaIdx: day.zhiIndex, xiaType: 'zhi' },
        { shangIdx: 0, xiaIdx: 0, xiaType: 'zhi' },
    ];
    // 依次取上神：二课以初课上神为下神，四课以三课上神为下神
    keInputs[1] = { shangIdx: tianPanIdx[keInputs[0].shangIdx], xiaIdx: keInputs[0].shangIdx, xiaType: 'zhi' };
    keInputs[3] = { shangIdx: tianPanIdx[keInputs[2].shangIdx], xiaIdx: keInputs[2].shangIdx, xiaType: 'zhi' };
    // ── 5. 三传（九宗门） ──
    const san = computeSanChuan({
        tianPanIdx,
        dayGanIndex: day.ganIndex,
        dayZhiIndex: day.zhiIndex,
        yueJiangIndex,
        zhanShiIndex,
        ke: keInputs,
    });
    // ── 6. 空亡 ──
    const [kongA, kongB] = xunKong(day.ganIndex, day.zhiIndex);
    const kongWang = [zhiOf(kongA), zhiOf(kongB)];
    // ── 7. 十二天将 ──
    const jiang = buildTianJiang(day.ganIndex, zhanShiIndex, tianPan);
    // 四课对象
    const ke = keInputs.map((k, i) => {
        const shang = zhiOf(k.shangIdx);
        return {
            index: i + 1,
            name: KE_NAMES[i],
            shang,
            shangIndex: k.shangIdx,
            xia: k.xiaType === 'gan' ? ganOf(k.xiaIdx) : zhiOf(k.xiaIdx),
            xiaIndex: k.xiaIdx,
            xiaType: k.xiaType,
            tianJiang: jiang.byTianPanZhi[shang] ?? '',
            liuQin: liuQinOfZhi(day.ganIndex, k.shangIdx),
            kong: kongWang.includes(shang),
        };
    });
    // 三传对象
    const chuan = san.chuan.map((z, i) => {
        const zhi = zhiOf(z);
        const dg = dunGan(day.ganIndex, day.zhiIndex, z);
        const dgIdx = dg ? ganIndex(dg) : -1;
        return {
            position: CHUAN_NAMES[i],
            zhi,
            zhiIndex: z,
            dunGan: dg,
            tianJiang: jiang.byTianPanZhi[zhi] ?? '',
            liuQin: liuQinOfZhi(day.ganIndex, z),
            kong: kongWang.includes(zhi),
            changSheng: dgIdx >= 0 ? changSheng(dgIdx, z) : null,
        };
    });
    // ── 8. 课体 / 神煞 ──
    const keTi = buildKeTi({
        san,
        chuan,
        dayGanIndex: day.ganIndex,
        dayZhiIndex: day.zhiIndex,
        ganShangIndex: keInputs[0].shangIdx,
        zhiShangIndex: keInputs[2].shangIdx,
    });
    const shenSha = buildShenSha({
        nianZhiIndex: year.zhiIndex,
        yueZhiIndex: month.zhiIndex,
        dayZhiIndex: day.zhiIndex,
        dayGanIndex: day.ganIndex,
        zhanShiIndex,
        chuanZhi: chuan.map((c) => c.zhi),
        keShang: ke.map((k) => k.shang),
    });
    // ── 9. 盘面骨架（不含应期 / 本命 / 断课） ──
    const core = {
        time: date.toISOString(),
        siZhu,
        siZhuText,
        yueJiang: {
            qi,
            qiTime: qiTime.toISOString(),
            zhi: yueJiang.zhi,
            name: yueJiang.name,
        },
        zhanShi: zhiOf(zhanShiIndex),
        dayNight: jiang.dayNight,
        pan: {
            diPan: [...ZHI],
            tianPan,
            zhanShi: zhiOf(zhanShiIndex),
            yueJiang: yueJiang.zhi,
            layout: `${yueJiang.zhi}加${zhiOf(zhanShiIndex)}`,
        },
        guiRenGong: jiang.guiRenGong,
        jiangDirection: jiang.direction,
        ke,
        chuan,
        keTi,
        kongWang,
        xun: xunName(day.ganIndex, day.zhiIndex),
        tianJiang: jiang.items,
        shenSha,
    };
    /** 应期（通行简法） */
    const yingQi = computeYingQi(core);
    /** 本命 / 行年（可选；出生年份非法时静默忽略，不影响排盘） */
    let benMing;
    if (opts.birthYear !== undefined) {
        try {
            benMing = computeBenMing({
                time: core.time,
                tianPan,
                tianJiang: jiang.items,
                kongWang,
                dayGanIndex: day.ganIndex,
            }, opts.birthYear, opts.gender ?? '男');
        }
        catch {
            benMing = undefined;
        }
    }
    const interpretation = interpret({
        siZhuText,
        dayGan: day.gan,
        dayZhi: day.zhi,
        zhanShi: zhiOf(zhanShiIndex),
        keTi,
        ke,
        chuan,
        kongWang,
        shenSha,
        benMing,
    });
    return { ...core, yingQi, benMing, interpretation };
}
// ════════════════════════════════════════════════════════════
// 展示辅助
// ════════════════════════════════════════════════════════════
/** 历史记录 lunar_text 用：四柱摘要（≤64 字） */
export function daliurenLunarText(r) {
    return `${r.siZhuText} · ${r.yueJiang.zhi}将${r.yueJiang.name}`;
}
/** 一句话摘要：课体 + 三传 + 吉凶（历史列表 / 卡片展示用） */
export function daliurenBrief(r) {
    const chuan = r.chuan.map((c) => c.zhi).join('');
    return `${r.keTi.name} · 三传${chuan} · ${r.interpretation.fortune}`;
}
/** 大六壬 input（存档用）：时间 ISO */
export function daliurenInput(date) {
    return { datetime: date.toISOString() };
}
/** 是否阳日（供 UI/文案使用） */
export function isYangDay(r) {
    return isYangGan(r.siZhu.day.ganIndex);
}
//# sourceMappingURL=engine.js.map